import axios from "axios";
import { axiosInstance } from "contexts/axiosApiInstance";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";

/** Mirrors the serde shapes in `server/src/api/search.rs`. */
export interface SearchModelResult {
  id: string;
  name: string;
  description: string | null;
  /** `"Owner"` or `"Shared"` — rendered below the model name. */
  relation: string;
  relevance_score: number;
}

export interface SearchTableResult {
  id: string;
  name: string;
  description: string | null;
  /** `name: null` ⇔ the caller cannot view the parent model (or it is
   *  trashed) ⇔ no data view — the row renders quiet-locked. */
  parent_model: { id: string; name: string | null };
  /** Free-floating computed table: lives in the overview, not a model.
   *  `parent_model.name` is always null for these; the row opens the
   *  overview rather than a model. */
  is_computed: boolean;
  /** Omitted on the wire when unknown — absence ≠ 0. */
  link_count?: number;
  relevance_score: number;
}

export interface SearchTeamResult {
  id: string;
  name: string;
  description: string | null;
  member_count: number;
  relevance_score: number;
}

export interface SearchDatasetResult {
  id: string;
  name: string;
  description: string | null;
  /** Advisory count of members the caller can currently discover. */
  member_count: number;
  /** `true` if the caller owns the dataset, else it is shared with them. */
  is_owner: boolean;
  relevance_score: number;
}

export interface SearchResponse {
  models: SearchModelResult[];
  tables: SearchTableResult[];
  teams: SearchTeamResult[];
  datasets: SearchDatasetResult[];
}

/** 429 from the per-keystroke governor — transient; callers keep stale results. */
export class SearchRateLimitedError extends Error {
  constructor() {
    super("search rate limited");
    this.name = "SearchRateLimitedError";
  }
}

export const searchApp = async (
  q: string,
  signal: AbortSignal,
): Promise<SearchResponse> => {
  try {
    const activeOrgId = useOrgContextStore.getState().activeOrgId;
    const response = await axiosInstance.get<SearchResponse>("/api/search", {
      params: { q, ...(activeOrgId ? { org_id: activeOrgId } : {}) },
      signal,
    });
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 429) {
      throw new SearchRateLimitedError();
    }
    throw err;
  }
};

/** Mirrors `server/src/api/tables/columns.rs`. */
export interface TableColumnsResponse {
  columns: {
    id: string;
    name: string | null;
    /** `PhysicalKind` display label; empty for not-yet-typed columns. */
    type: string;
  }[];
}

export const fetchTableColumns = async (
  tableId: string,
): Promise<TableColumnsResponse> => {
  const response = await axiosInstance.get<TableColumnsResponse>(
    `/api/tables/${encodeURIComponent(tableId)}/columns`,
  );
  return response.data;
};
